"use client";

import React from "react";

import {
  ErrorBoundary as _ErrorBoundary,
  type FallbackProps as ReactFallbackProps,
} from "react-error-boundary";

import { Button } from "src/components/ui/button";
import { HttpError } from "src/utils/errors";

export interface FallbackProps {
  title?: string;
  renderMessage?: () => React.ReactNode;
}

// This component provides the default error message
const FallbackMessage = () => {
  const openChat = () => {
    if (window.Intercom) {
      window.Intercom("show");
    }
  };

  return (
    <>
      An unexpected error occurred. Please try again or{" "}
      <span
        onClick={openChat}
        className="underline cursor-pointer text-primary"
      >
        chat with us
      </span>{" "}
      if the problem persists.
    </>
  );
};

// This component handles the 403 Forbidden error message
const ForbiddenMessage = () => {
  const openChat = () => {
    if (window.Intercom) {
      window.Intercom("show");
    }
  };

  return (
    <>
      You don't have permission to access this resource. If you believe this is
      an error,{" "}
      <span
        onClick={openChat}
        className="underline cursor-pointer text-primary"
      >
        chat with us
      </span>{" "}
      for assistance.
    </>
  );
};

// This component handles the 404 Not Found error message
const NotFoundMessage = () => {
  const openChat = () => {
    if (window.Intercom) {
      window.Intercom("show");
    }
  };

  return (
    <>
      The resource you're looking for couldn't be found. If you believe this is
      an error,{" "}
      <span
        onClick={openChat}
        className="underline cursor-pointer text-primary"
      >
        chat with us
      </span>{" "}
      for assistance.
    </>
  );
};

export const Fallback = ({
  error,
  resetErrorBoundary,
}: ReactFallbackProps): React.ReactElement => {
  let title = "Something went wrong";
  let Message = FallbackMessage;
  // Flag to determine if we should show the reload button
  let showReloadButton = true;

  if (error instanceof HttpError) {
    // Determine which message to show based on error type
    if (error.isForbidden()) {
      title = "Access Denied";
      Message = ForbiddenMessage;
      showReloadButton = false; // Don't show reload for permission errors
    } else if (error.isNotFound()) {
      title = "Not Found";
      Message = NotFoundMessage;
      showReloadButton = false; // Don't show reload for not found errors
    }
  }

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div className="flex flex-1 items-center justify-center flex-col text-center w-full mx-auto max-w-[425px]">
      <div className="flex flex-col gap-2 mb-10">
        <h6 className="text-base font-medium leading-[1.56]">{title}</h6>
        <p className="text-sm leading-normal text-text-secondary mb-4">
          <Message />
        </p>
      </div>
      {showReloadButton && (
        <Button variant="outline" onClick={handleReload}>
          Reload page
        </Button>
      )}
    </div>
  );
};

interface ErrorBoundaryProps extends React.PropsWithChildren {
  fallbackProps?: FallbackProps;
  // When any value in this array changes, the boundary resets and re-renders
  // its children. Used so widgets recover from a render failure once the entity
  // or effective date changes instead of staying stuck on the fallback.
  resetKeys?: unknown[];
}

const ErrorBoundary = ({
  children,
  fallbackProps,
  ...props
}: ErrorBoundaryProps): React.ReactElement => (
  <_ErrorBoundary {...props} FallbackComponent={Fallback}>
    {children}
  </_ErrorBoundary>
);
export default ErrorBoundary;
